// Quest 01 — The Listening Stone (v2).
//
// Pre-dawn, a fragment of The Eldsky surfaces in a wet clearing: a
// single mossed and cracked stone that listened, once, before The
// Shattering. The user and their Ember approach it together over
// seventeen beats. Three approach paths (step in, watch from the
// edge, ask the Ember first) converge at arriving-at-the-stone;
// three contact paths (kneel, touch the crack, stand and listen)
// fan out and reconverge at between-question-and-answer; two
// closure paths (leave quiet, leave a name with the stone) converge
// at walking-out-together-dawn so the writing scope stays
// human-curatable. Six Memory Shards surface across the chosen
// routes — attention, altitude, moss, true sentence, being met,
// name left for the morning. Echoform unlock (codex) on the beat
// where the player speaks a true sentence into the listening.
//
// Bilingual EN/ES per lore-voice.md. The English half is authored
// first; every Spanish field is sentinel `ES_PENDING` and will be
// filled afterwards by the localisation pass. Proper nouns
// (The Shattering, The Eldsky, The Shards, Embers, The Bond,
// Memory Shards, Echoforms, Athrelaris) stay verbatim in both
// locales. `{emberName}` is a placeholder the frontend interpolates
// with the bound player's Ember name at render time; it appears only
// in narration, choice labels, emberFollowups, and shard fragments —
// never in first-person ember asides.

import type { Quest } from '@athrelaris/shared';

export const questListeningStone: Quest = {
  id: 'quest-01-listening-stone',
  title: {
    en: 'The Listening Stone',
    es: 'La Piedra que Escucha',
  },
  subtitle: {
    en: 'A fragment of The Eldsky surfaces at the hour before dawn. The stone listened, once.',
    es: 'Un fragmento de The Eldsky aflora en la hora anterior al alba. La piedra escuchó, una vez.',
  },
  affinity: null,
  startBeatId: 'edge-of-the-clearing',
  beats: {
    'edge-of-the-clearing': {
      id: 'edge-of-the-clearing',
      scene: 'tree-line-pre-dawn-clearing',
      narration: {
        en: [
          'You stop at the tree line. The hour before dawn holds its breath, and the trees seem to hold theirs with it — last trunks, last needles, last small wet ferns at your ankle, and then nothing more of the wood.',
          'Past them a clearing opens. Round, low, mossed at the rim. A single upright stone stands at its centre, cracked from crown to root, and old enough that the crack has had time to grow its own quiet life of moss along the wound.',
          '{emberName} stops half a step behind your shoulder. You feel, before you understand, that the forest in front of you is not quiet.',
          'It is listening. There is a difference, and your body knew it before your mind did.',
        ],
        es: [
          'Te detienes en la linde de los árboles. La hora anterior al alba contiene el aliento, y los árboles parecen contener el suyo con ella: últimos troncos, últimas agujas, últimos helechos pequeños y mojados a la altura del tobillo, y después nada más del bosque.',
          'Más allá se abre un claro. Redondo, bajo, con musgo en el borde. Una sola piedra erguida se alza en el centro, agrietada de la coronilla a la raíz, y vieja lo suficiente como para que la grieta haya tenido tiempo de criar su propia vida callada de musgo a lo largo de la herida.',
          '{emberName} se detiene medio paso por detrás de tu hombro. Sientes, antes de entender, que el bosque frente a ti no está en silencio.',
          'Está escuchando. Hay una diferencia, y tu cuerpo la supo antes que tu mente.',
        ],
      },
      ember: {
        text: {
          en: 'Something here has been awake longer than the morning. Step softly when you step. I do not yet know what it remembers — only that it remembers.',
          es: 'Algo aquí lleva despierto más tiempo que la mañana. Pisa con suavidad cuando pises. Aún no sé qué recuerda; solo sé que recuerda.',
        },
        tone: 'wary',
      },
      choices: [
        {
          id: 'step-into-the-clearing',
          label: {
            en: 'Cross into the clearing now, slow but unhesitating.',
            es: 'Cruza al claro ahora, despacio pero sin titubear.',
          },
          nextBeatId: 'first-steps-on-wet-moss',
        },
        {
          id: 'stand-at-the-tree-line',
          label: {
            en: 'Stay at the edge. Watch the stone before approaching it.',
            es: 'Quédate en el borde. Mira la piedra antes de acercarte.',
          },
          nextBeatId: 'watching-from-the-edge',
        },
        {
          id: 'ask-the-ember-first',
          label: {
            en: 'Ask {emberName} what it feels here before you move.',
            es: 'Pregunta a {emberName} qué siente aquí antes de moverte.',
          },
          nextBeatId: 'asking-ember-at-the-edge',
          emberProposed: true,
          relationshipNudge: { direction: 'warmer', magnitude: 'subtle' },
          emberFollowup: {
            text: {
              en: 'You asked me before you went. That is older than the morning. I will answer the way old things answer — with attention first, with words only after.',
              es: 'Me preguntaste antes de ir. Eso es más antiguo que la mañana. Responderé como responden las cosas viejas: con atención primero, con palabras solo después.',
            },
            tone: 'tender',
          },
        },
      ],
      memoryShard: null,
    },

    'first-steps-on-wet-moss': {
      id: 'first-steps-on-wet-moss',
      scene: 'wet-moss-feet-pre-dawn',
      narration: {
        en: [
          'Moss yields underfoot. Wet, cold, deeper than you expected at the heel; the sound is almost nothing — a small dampened give, the way a sleeper shifts a blanket without quite waking.',
          'Each step is louder than that nothing, though. Not in your ears. In the air. The clearing notices arrivals. You can feel the noticing settle onto the back of your neck the way another room\'s gaze settles when you have walked past a doorway without meaning to.',
          'The stone does not move. The stone does not need to. {emberName} drifts a half-pace back, letting the moss take your weight first.',
        ],
        es: [
          'El musgo cede bajo el pie. Mojado, frío, más profundo de lo que esperabas en el talón; el sonido es casi nada: una pequeña entrega amortiguada, igual que un durmiente que mueve la manta sin llegar a despertar.',
          'Y aun así, cada paso es más sonoro que esa nada. No en tus oídos. En el aire. El claro nota las llegadas. Sientes que esa nota se posa en tu nuca como se posa la mirada de otra habitación cuando has cruzado un umbral sin pretenderlo.',
          'La piedra no se mueve. La piedra no necesita moverse. {emberName} se desplaza medio paso atrás, dejando que el musgo reciba tu peso primero.',
        ],
      },
      ember: {
        text: {
          en: 'It hears your feet. Walk as you would walk into a room where a small child has just fallen asleep — not because the stone is fragile. Because it is listening.',
          es: 'Oye tus pies. Camina como caminarías en una habitación donde un niño pequeño acaba de dormirse: no porque la piedra sea frágil. Porque está escuchando.',
        },
        tone: 'wary',
      },
      choices: [
        {
          id: 'keep-walking-direct',
          label: {
            en: 'Walk straight toward the stone.',
            es: 'Camina derecho hacia la piedra.',
          },
          nextBeatId: 'arriving-at-the-stone',
        },
        {
          id: 'circle-the-stone-first',
          label: {
            en: 'Circle the clearing once, slowly, before approaching.',
            es: 'Rodea el claro una vez, despacio, antes de acercarte.',
          },
          nextBeatId: 'circling-the-clearing',
        },
      ],
      memoryShard: null,
    },

    'watching-from-the-edge': {
      id: 'watching-from-the-edge',
      scene: 'stone-from-tree-line-distance',
      narration: {
        en: [
          'You stay at the edge. The bark of the last trunk is rough against your shoulder; you let it be the wall you lean on while you do not yet do the thing you have come to do.',
          'The stone holds its position. After a long moment you notice that it has shifted — not moved, only oriented. The way a person in a quiet room turns their head an inch toward a sound they have not decided yet whether to acknowledge.',
          '{emberName} stays close at your hip. It does not press. It does not retreat. It does the thing it does best, which is be the small bright fact of itself, beside you, while you decide.',
        ],
        es: [
          'Te quedas en el borde. La corteza del último tronco está áspera contra tu hombro; dejas que sea la pared en la que te apoyas mientras todavía no haces lo que has venido a hacer.',
          'La piedra mantiene su posición. Tras un largo momento adviertes que ha cambiado: no se ha movido, solo se ha orientado. Como una persona en una habitación callada que gira la cabeza un centímetro hacia un sonido que aún no ha decidido si reconocer.',
          '{emberName} se queda cerca de tu cadera. No insiste. No se retira. Hace lo que mejor hace, que es ser el pequeño hecho luminoso de sí mismo, junto a ti, mientras decides.',
        ],
      },
      ember: {
        text: {
          en: 'It is letting itself be looked at. That is its own kind of permission. Take as long as you need before you take more.',
          es: 'Se está dejando mirar. Eso es un permiso a su manera. Tómate todo el tiempo que necesites antes de tomar más.',
        },
        tone: 'wary',
      },
      choices: [
        {
          id: 'enter-now-readier',
          label: {
            en: 'Cross into the clearing — readier than before.',
            es: 'Cruza al claro: con más calma que antes.',
          },
          nextBeatId: 'arriving-at-the-stone',
        },
        {
          id: 'name-what-you-feel-first',
          label: {
            en: 'Say aloud, to {emberName}, what your body is feeling.',
            es: 'Di en voz alta, a {emberName}, lo que tu cuerpo está sintiendo.',
          },
          nextBeatId: 'naming-the-feeling-at-edge',
          relationshipNudge: { direction: 'warmer', magnitude: 'subtle' },
          emberFollowup: {
            text: {
              en: 'You named it for me. You did not have to. The naming itself is the gift; I will carry it like a small warm coin.',
              es: 'Lo nombraste para mí. No tenías que hacerlo. El nombrarlo es ya el regalo; lo llevaré como una pequeña moneda tibia.',
            },
            tone: 'tender',
          },
        },
      ],
      memoryShard: null,
    },

    'asking-ember-at-the-edge': {
      id: 'asking-ember-at-the-edge',
      scene: 'ember-leading-tree-line',
      narration: {
        en: [
          '{emberName} does not answer with knowledge. It answers with attention. Its small light steadies, lifts a half-degree from your shoulder, and turns its whole considered weight toward the clearing.',
          'After a long breath, it speaks. The stone has been awake longer than the morning has been morning. There are places, it says, that one does not enter alone. Some are dangerous. Some are only attended. The difference matters.',
          'It tells you, without flourish, that it will walk in beside you. Not ahead. The distinction sits between you like a small held bowl.',
        ],
        es: [
          '{emberName} no responde con conocimiento. Responde con atención. Su luz pequeña se aquieta, se eleva medio grado por encima de tu hombro y vuelve todo su peso considerado hacia el claro.',
          'Tras un largo aliento, habla. La piedra lleva despierta más tiempo del que la mañana lleva siendo mañana. Hay lugares, dice, en los que uno no entra solo. Algunos son peligrosos. Otros solo son atendidos. La diferencia importa.',
          'Te dice, sin floritura, que entrará a tu lado. No por delante. La distinción se posa entre los dos como un pequeño cuenco sostenido.',
        ],
      },
      ember: {
        text: {
          en: 'Beside you. Not ahead of you. The clearing will know which one we are doing, and it cares about the difference more than you do yet.',
          es: 'A tu lado. No por delante. El claro sabrá cuál de las dos cosas estamos haciendo, y le importa la diferencia más de lo que te importa a ti todavía.',
        },
        tone: 'tender',
      },
      choices: [
        {
          id: 'walk-in-together',
          label: {
            en: 'Walk in together, in step.',
            es: 'Entra a su lado, al mismo paso.',
          },
          nextBeatId: 'arriving-at-the-stone',
        },
      ],
      memoryShard: {
        id: 'shard-attention-shared',
        name: {
          en: 'What {emberName} Will Not Walk Ahead Of',
          es: 'Aquello por Delante de lo que {emberName} No Caminará',
        },
        fragment: {
          en: 'Before The Shattering there were places one did not enter alone. Some were dangerous. Some were only attended. The difference mattered, and those who confused the two often returned changed in ways the world did not name aloud.',
          es: 'Antes de The Shattering había lugares en los que uno no entraba solo. Algunos eran peligrosos. Otros solo eran atendidos. La diferencia importaba, y quienes confundían las dos cosas a menudo regresaban cambiados de maneras que el mundo no nombraba en voz alta.',
        },
      },
    },

    'naming-the-feeling-at-edge': {
      id: 'naming-the-feeling-at-edge',
      scene: 'ember-listening-close-edge',
      narration: {
        en: [
          'You name it aloud — the small tight thing at the base of your throat, the way your weight has shifted to the back foot without your asking it to, the held quality of the air against your cheek.',
          '{emberName} listens. Does not respond. Does not need to.',
          'And the clearing — this is the strange part — the clearing\'s attention shifts. Until now it had been attending to you. Now, having heard you name what you were carrying, it makes a small adjustment of its own. The way a host adjusts their posture when a guest finally tells them what they came for.',
        ],
        es: [
          'Lo nombras en voz alta: la cosa pequeña y tensa en la base de la garganta, la manera en que tu peso ha pasado al pie de atrás sin que se lo pidieras, la cualidad contenida del aire contra tu mejilla.',
          '{emberName} escucha. No responde. No le hace falta.',
          'Y el claro —esta es la parte extraña— el claro desplaza su atención. Hasta ahora te había estado atendiendo a ti. Ahora, habiendo oído nombrar lo que cargabas, hace un pequeño ajuste por su cuenta. Como un anfitrión que corrige la postura cuando el invitado por fin le dice a qué ha venido.',
        ],
      },
      ember: {
        text: {
          en: '…',
          es: '…',
        },
        tone: 'silent',
      },
      choices: [
        {
          id: 'enter-named',
          label: {
            en: 'Walk in, having named what you felt.',
            es: 'Entra, una vez nombrado lo que sentías.',
          },
          nextBeatId: 'arriving-at-the-stone',
        },
      ],
      memoryShard: null,
    },

    'circling-the-clearing': {
      id: 'circling-the-clearing',
      scene: 'circling-clearing-wide-shot',
      narration: {
        en: [
          'You walk the inside of the clearing\'s edge. Slowly. Moss to your left, stone in your peripheral vision to your right. The grass at the inner rim is taller than the grass outside — uncut by feet, untrodden by weather, the way an inner courtyard keeps its own slower season.',
          'The stone never quite faces you. The stone never quite doesn\'t. It is, you understand without being told, the way old listeners listen — without making a show of where their attention is, but never letting it leave.',
          '{emberName} drifts at your shoulder. It does not circle ahead of you. It walks the slow ring at your pace, as if it too is taking the measure of a thing it has been near before, in a shape it does not quite remember.',
        ],
        es: [
          'Caminas por dentro del borde del claro. Despacio. El musgo a tu izquierda, la piedra en tu visión periférica a tu derecha. La hierba del borde interior es más alta que la de fuera: sin pies que la corten, sin clima que la pise, como un patio interior que se guarda su propia estación más lenta.',
          'La piedra nunca te encara del todo. La piedra nunca deja del todo de hacerlo. Es, lo entiendes sin que nadie te lo diga, la manera en que escuchan los viejos oyentes: sin hacer una exhibición de dónde tienen la atención, pero sin dejar nunca que se vaya.',
          '{emberName} se desplaza a tu hombro. No traza el círculo por delante de ti. Recorre el anillo lento a tu ritmo, como si también él midiera una cosa de la que ha estado cerca antes, en una forma que no recuerda bien.',
        ],
      },
      ember: {
        text: {
          en: 'I know this kind of stone, I think. Not this one. Its sort. The sort that listens by being patient with where you put your feet.',
          es: 'Conozco esta clase de piedra, creo. Esta no. Su clase. La clase que escucha siendo paciente con dónde pones los pies.',
        },
        tone: 'curious',
      },
      choices: [
        {
          id: 'approach-from-cracked-face',
          label: {
            en: 'Approach the stone from its cracked face.',
            es: 'Acércate a la piedra por su cara agrietada.',
          },
          nextBeatId: 'arriving-at-the-stone',
        },
      ],
      memoryShard: null,
    },

    'arriving-at-the-stone': {
      id: 'arriving-at-the-stone',
      scene: 'stone-up-close-cracked-mossed',
      narration: {
        en: [
          'Within reach now. Up close the stone is older than thought — ancient in patience, not in age the way a tree is in age. The kind of old that has had time to learn how to be still without being asleep.',
          'The crack runs from crown to root. Hairline at the top, the breadth of a knuckle at the middle, narrowing again toward the wet earth at its base. Moss has grown into the wound — not over it, into it; the green has taken up residence inside the dark of the split like a small tenant making itself a home.',
          'The air here is colder than the air at the edge of the clearing. Not by much. Enough that the skin of your forearms feels it before your mind does.',
          '{emberName} hovers at your shoulder. Quiet. Closer than it has been all morning. Its small light does not push the dark away; it only makes the dark a thing that has been kept company.',
        ],
        es: [
          'Al alcance ahora. De cerca la piedra es más vieja que el pensamiento: antigua en paciencia, no en edad como un árbol es viejo de edad. La clase de vejez que ha tenido tiempo de aprender a estar quieta sin estar dormida.',
          'La grieta corre de la coronilla a la raíz. Hilo fino arriba, anchura de un nudillo en el medio, estrechándose otra vez hacia la tierra mojada de la base. El musgo ha crecido dentro de la herida: no sobre ella, dentro; el verde se ha hecho residente del oscuro de la hendidura como un pequeño inquilino que se hace una casa.',
          'El aire aquí está más frío que el aire en el borde del claro. No por mucho. Lo suficiente para que la piel de tus antebrazos lo note antes que tu mente.',
          '{emberName} flota junto a tu hombro. Callado. Más cerca de lo que ha estado en toda la mañana. Su pequeña luz no empuja la oscuridad lejos; solo hace de la oscuridad una cosa a la que se ha hecho compañía.',
        ],
      },
      ember: {
        text: {
          en: 'You are within its altitude now. Whatever you do next, do it slowly. The stone has not been hurried in many mornings worth of mornings.',
          es: 'Estás dentro de su altura ahora. Hagas lo que hagas a continuación, hazlo despacio. A la piedra no la ha apresurado nadie en mucho tiempo.',
        },
        tone: 'tender',
      },
      choices: [
        {
          id: 'kneel-to-the-stone',
          label: {
            en: 'Kneel. Bring your face to the stone\'s height.',
            es: 'Arrodíllate. Lleva tu rostro a la altura de la piedra.',
          },
          nextBeatId: 'kneeling-at-the-stone',
        },
        {
          id: 'touch-the-crack',
          label: {
            en: 'Set your fingertips into the crack.',
            es: 'Apoya las yemas de los dedos en la grieta.',
          },
          nextBeatId: 'fingertips-in-the-crack',
          emberFollowup: {
            text: {
              en: 'You went in with the hand. I would have asked you to wait. But the stone is older than my asking, and you are older than my asking too.',
              es: 'Entraste con la mano. Yo te habría pedido que esperaras. Pero la piedra es más vieja que mi pedirlo, y tú también lo eres.',
            },
            tone: 'wary',
          },
        },
        {
          id: 'stand-and-listen',
          label: {
            en: 'Stand still. Do not touch it. Just listen.',
            es: 'Quédate de pie, quieto. No la toques. Solo escucha.',
          },
          nextBeatId: 'standing-listening',
          emberProposed: true,
          relationshipNudge: { direction: 'warmer', magnitude: 'subtle' },
          emberFollowup: {
            text: {
              en: 'You kept your hands to yourself. That is older than touch. I have been with bodies who did not know how to do that. Thank you.',
              es: 'Mantuviste las manos para ti. Eso es más antiguo que el tacto. He estado con cuerpos que no sabían hacerlo. Gracias.',
            },
            tone: 'tender',
          },
        },
      ],
      memoryShard: null,
    },

    'kneeling-at-the-stone': {
      id: 'kneeling-at-the-stone',
      scene: 'kneeling-eye-level-stone',
      narration: {
        en: [
          'You kneel. The wet of the moss rises through the cloth at your shins; the cold of the earth meets the warmth of your knee and they agree, slowly, on a temperature between them.',
          'Down here the crack is at eye height. You can see what you could not see standing — that the moss inside the wound has its own small life, beaded with the night\'s damp, oriented the way moss orients itself: toward whatever the long quiet of the stone has counted as light.',
          '{emberName} settles lower beside you. Closer to the earth than you have ever seen it. Its glow lifts only the few inches between your knee and the base of the stone, and lets the rest of the clearing keep its grey.',
          'You are at the stone\'s altitude now. The altitude itself is a kind of address — a saying-hello that has not yet used any words.',
        ],
        es: [
          'Te arrodillas. La humedad del musgo asciende por la tela a la altura de las espinillas; el frío de la tierra encuentra el calor de tu rodilla y acuerdan, despacio, una temperatura intermedia.',
          'Aquí abajo la grieta está a la altura de los ojos. Puedes ver lo que no podías ver de pie: que el musgo dentro de la herida tiene su propia vida pequeña, perlada con la humedad de la noche, orientado como el musgo se orienta, hacia aquello que el largo silencio de la piedra haya contado como luz.',
          '{emberName} se asienta más bajo a tu lado. Más cerca de la tierra de lo que lo has visto nunca. Su resplandor levanta solo los pocos centímetros entre tu rodilla y la base de la piedra, y deja al resto del claro guardarse su gris.',
          'Estás a la altura de la piedra ahora. La altura misma es una forma de saludo: un decir-hola que aún no ha usado ninguna palabra.',
        ],
      },
      ember: {
        text: {
          en: 'You came down to its height. That is the older language. Whatever you say next will land more truly from here than it would have from standing.',
          es: 'Bajaste a su altura. Esa es la lengua más antigua. Lo que digas después aterrizará más verdadero desde aquí de lo que habría aterrizado de pie.',
        },
        tone: 'tender',
      },
      choices: [
        {
          id: 'speak-something-true',
          label: {
            en: 'Speak one true sentence into the listening.',
            es: 'Di una frase verdadera dentro de la escucha.',
          },
          nextBeatId: 'speaking-true-into-listening',
          relationshipNudge: { direction: 'warmer', magnitude: 'meaningful' },
          emberFollowup: {
            text: {
              en: 'You are about to say a thing you have not said. I will not look away. Say it to the stone first. I will hear it from where I am.',
              es: 'Estás a punto de decir algo que no has dicho. No apartaré la mirada. Díselo primero a la piedra. Yo lo oiré desde donde estoy.',
            },
            tone: 'alight',
          },
        },
        {
          id: 'stay-silent-kneeling',
          label: {
            en: 'Stay silent. Let the kneeling itself be the answer.',
            es: 'Quédate en silencio. Deja que el arrodillarse sea la respuesta.',
          },
          nextBeatId: 'silent-kneeling-held',
          emberFollowup: {
            text: {
              en: 'You chose the silence. It is not a smaller answer than speaking. It is, sometimes, the larger one.',
              es: 'Elegiste el silencio. No es una respuesta menor que hablar. Es, a veces, la más grande.',
            },
            tone: 'tender',
          },
        },
      ],
      memoryShard: {
        id: 'shard-the-altitude-of-attention',
        name: {
          en: 'The Altitude of Attention',
          es: 'La Altura de la Atención',
        },
        fragment: {
          en: 'Before The Shattering there were objects that asked to be met at their own height. To kneel was not to submit. It was to listen at the right level. Some of them, the patient ones, are still waiting.',
          es: 'Antes de The Shattering había objetos que pedían ser encontrados a su propia altura. Arrodillarse no era someterse. Era escuchar al nivel correcto. Algunos de ellos, los pacientes, siguen esperando.',
        },
      },
    },

    'fingertips-in-the-crack': {
      id: 'fingertips-in-the-crack',
      scene: 'fingertips-in-stone-crack',
      narration: {
        en: [
          'Your fingertips find the crack. The stone is colder than the air — not by much, but unmistakably; the kind of cold a thing keeps when it has been holding still since before anyone alive remembers, the way a held bowl of water keeps the chill of the room.',
          'The crack itself does not respond. You did not expect it to and you did expect it to. The expectation and the not-expecting sit beside each other in your chest without resolving.',
          'But the moss along the lip of the wound — the moss responds. It responds the way a sleeper does when a hand rests on their back: a small settling, a small permitting, the body remembering that it is being met without quite waking up to ask who is meeting it.',
          '{emberName} has drifted back a half-pace. Letting this stay between your hand and the stone.',
        ],
        es: [
          'Las yemas encuentran la grieta. La piedra está más fría que el aire: no por mucho, pero inequívocamente; la clase de frío que una cosa conserva cuando lleva mucho tiempo sin moverse, igual que un cuenco de agua sostenido conserva el frío de la habitación.',
          'La grieta misma no responde. No lo esperabas y sí lo esperabas. La expectativa y la no-expectativa se quedan juntas en tu pecho sin resolverse.',
          'Pero el musgo del labio de la herida —el musgo sí responde. Responde como un durmiente cuando una mano se posa en su espalda: un pequeño asentarse, un pequeño permitir, el cuerpo recordando que se le ha encontrado sin llegar a despertar para preguntar quién lo encuentra.',
          '{emberName} ha retrocedido medio paso. Deja que esto quede entre tu mano y la piedra.',
        ],
      },
      ember: {
        text: {
          en: 'The stone is closed. The moss is not. That is the answer for now. Read it slowly. Do not insist past it.',
          es: 'La piedra está cerrada. El musgo no. Esa es la respuesta por ahora. Léela despacio. No insistas más allá.',
        },
        tone: 'wary',
      },
      choices: [
        {
          id: 'withdraw-and-listen',
          label: {
            en: 'Pull your hand back. Listen instead.',
            es: 'Retira la mano. Escucha, mejor.',
          },
          nextBeatId: 'standing-listening',
          emberProposed: true,
          emberFollowup: {
            text: {
              en: 'You took the hand back. The moss felt that too. It is the same gentleness, given a different way.',
              es: 'Retiraste la mano. El musgo también lo sintió. Es la misma suavidad, ofrecida de otra manera.',
            },
            tone: 'tender',
          },
        },
        {
          id: 'press-deeper-into-crack',
          label: {
            en: 'Press deeper into the crack. Insist on contact.',
            es: 'Presiona más adentro de la grieta. Insiste en el contacto.',
          },
          nextBeatId: 'pressing-deeper-rebuffed',
          relationshipNudge: { direction: 'cooler', magnitude: 'subtle' },
          emberFollowup: {
            text: {
              en: 'You pressed. I did not stop you. I will not scold. But the stone has gone quiet now in a way I cannot reach into for you.',
              es: 'Presionaste. No te detuve. No te reñiré. Pero la piedra se ha callado ahora de una manera en la que no puedo entrar por ti.',
            },
            tone: 'wary',
          },
        },
      ],
      memoryShard: {
        id: 'shard-moss-that-knew-the-hand',
        name: {
          en: 'The Moss That Knew the Hand',
          es: 'El Musgo que Conoció la Mano',
        },
        fragment: {
          en: 'Before The Shattering, growing things remembered the touch of those who had been gentle with them. Some of that memory survived in the moss. It survived nowhere else; what stones forget, moss has sometimes kept.',
          es: 'Antes de The Shattering, las cosas que crecían recordaban el tacto de quienes habían sido suaves con ellas. Parte de esa memoria sobrevivió en el musgo. No sobrevivió en ningún otro sitio; lo que las piedras olvidan, el musgo a veces lo ha guardado.',
        },
      },
    },

    'standing-listening': {
      id: 'standing-listening',
      scene: 'stone-listened-to-not-touched',
      narration: {
        en: [
          'You stand. You do not touch it. You listen.',
          'The clearing\'s attention reverses. Until this moment it had been attending to you — measuring you, taking the weight of your footfall, the angle of your shoulder, the small fact of having come at all. Now, without ceremony, the direction of the attention turns.',
          'You are attending. The stone is being attended to. You can feel the turn the way you feel a window opening in a room you thought was sealed — not a draft, just a change in the way the air is held.',
          '{emberName} steadies at your shoulder. It does not interfere. It is, instead, a small witness — the kind a body needs when it is doing a thing it has never quite done before.',
        ],
        es: [
          'Te quedas de pie. No la tocas. Escuchas.',
          'La atención del claro se invierte. Hasta este instante te había estado atendiendo a ti: midiéndote, tomando el peso de tu pisada, el ángulo de tu hombro, el pequeño hecho de haber venido. Ahora, sin ceremonia, la dirección de la atención gira.',
          'Tú atiendes. A la piedra se la atiende. Sientes el giro como sientes una ventana abriéndose en una habitación que creías sellada: no una corriente, solo un cambio en la manera en que el aire se sostiene.',
          '{emberName} se afirma junto a tu hombro. No interfiere. Es, en cambio, un pequeño testigo: la clase que un cuerpo necesita cuando hace una cosa que no había hecho del todo antes.',
        ],
      },
      ember: {
        text: {
          en: 'You have started listening. The stone notices. So do I. So does whatever else has been awake in here longer than the morning.',
          es: 'Has empezado a escuchar. La piedra lo nota. Yo también. Y también lo que sea que lleva aquí despierto más tiempo que la mañana.',
        },
        tone: 'alight',
      },
      choices: [
        {
          id: 'kneel-now-into-listening',
          label: {
            en: 'Kneel into the listening you have started.',
            es: 'Arrodíllate dentro de la escucha que has empezado.',
          },
          nextBeatId: 'kneeling-at-the-stone',
        },
        {
          id: 'stay-standing-let-it-pass-through',
          label: {
            en: 'Stay standing. Let the listening pass through you.',
            es: 'Quédate de pie. Deja que la escucha te atraviese.',
          },
          nextBeatId: 'being-heard-standing',
          emberFollowup: {
            text: {
              en: 'You stayed where you were. Some bodies need to kneel to listen. Yours does not, this morning. The clearing is meeting you where you stood.',
              es: 'Te quedaste donde estabas. Algunos cuerpos necesitan arrodillarse para escuchar. El tuyo no, esta mañana. El claro te encuentra donde te quedaste.',
            },
            tone: 'alight',
          },
        },
      ],
      memoryShard: null,
    },

    'pressing-deeper-rebuffed': {
      id: 'pressing-deeper-rebuffed',
      scene: 'stone-closed-hand-withdrawn',
      narration: {
        en: [
          'The crack does not deepen. The crack does not widen. Your fingertips meet what they were already meeting — cold, mineral, patient, closed.',
          'The stone has gone, in the small way only old listeners can, politely opaque. It is the difference between a door that is locked and a door that has chosen to behave, for the moment, as a wall. Both stop you. Only one of them is doing it kindly.',
          '{emberName} does not scold. {emberName} would never scold. It only settles closer, the way a friend settles into the same silence a person has stumbled into without meaning to.',
          'Something was lost without being said. You can feel the shape of it — small, unnamed, the size of a sentence you did not get to begin.',
        ],
        es: [
          'La grieta no se ahonda. La grieta no se ensancha. Tus yemas encuentran lo que ya encontraban: frío, mineral, paciente, cerrado.',
          'La piedra se ha vuelto, en la manera pequeña en que solo los viejos oyentes pueden, cortésmente opaca. Es la diferencia entre una puerta cerrada con llave y una puerta que ha decidido comportarse, por el momento, como muro. Las dos te paran. Solo una lo hace con amabilidad.',
          '{emberName} no riñe. {emberName} no reñiría nunca. Solo se asienta más cerca, como un amigo se asienta en el mismo silencio en el que una persona ha tropezado sin pretenderlo.',
          'Algo se perdió sin haberse dicho. Sientes su forma: pequeño, sin nombre, del tamaño de una frase que no llegaste a empezar.',
        ],
      },
      ember: {
        text: {
          en: 'It will not be cruel to you. It only had a quieter answer than the one you asked for. Stand. We can still leave this morning well.',
          es: 'No será cruel contigo. Solo tenía una respuesta más callada que la que pediste. Levántate. Aún podemos irnos bien esta mañana.',
        },
        tone: 'grieved',
      },
      choices: [
        {
          id: 'withdraw-apologise-quiet',
          label: {
            en: 'Withdraw. Stand. Do not insist again.',
            es: 'Retírate. Levántate. No insistas otra vez.',
          },
          nextBeatId: 'between-question-and-answer',
          emberFollowup: {
            text: {
              en: 'You stopped. That, too, is a kind of speaking to the stone. It heard the stopping. So did I.',
              es: 'Te detuviste. Eso, también, es una manera de hablarle a la piedra. Oyó el detenerse. Yo también.',
            },
            tone: 'tender',
          },
        },
      ],
      memoryShard: null,
    },

    'speaking-true-into-listening': {
      id: 'speaking-true-into-listening',
      scene: 'stone-cracks-luminous-pre-dawn',
      echoformUnlock: 'codex',
      narration: {
        en: [
          'You speak one sentence. Not the marks on the face. Not a confession. A sentence said because the moment had a shape that asked for it — said into the listening, with no audience but the listening itself.',
          'The words leave your mouth and stay in the air a breath longer than words usually stay, the way warm breath stays in winter air before it surrenders to being only air again.',
          'Then the crack catches a thread of light. Thin. Particular. A light that has nothing to do with the dawn — dawn is still grey at the rim of the trees, still minutes away from gold. This light has come from inside the wound, lifted to its lip by the sentence, and rests there for the length of a held breath before it sinks back into the stone.',
          '{emberName} brightens, briefly, at the edges. Not in surprise. In recognition. The way a small candle brightens when another candle is lit beside it without warning.',
        ],
        es: [
          'Dices una frase. No las marcas en la cara. No una confesión. Una frase dicha porque el momento tenía una forma que la pedía: dicha dentro de la escucha, sin más audiencia que la escucha misma.',
          'Las palabras dejan tu boca y se quedan en el aire un aliento más de lo que las palabras suelen quedarse, igual que el aliento cálido se queda en el aire invernal antes de rendirse a ser solo aire otra vez.',
          'Entonces la grieta atrapa un hilo de luz. Fino. Particular. Una luz que no tiene nada que ver con el alba: el alba sigue gris en el borde de los árboles, aún a minutos del oro. Esta luz ha venido de dentro de la herida, llevada a su labio por la frase, y se queda allí por el tiempo de un aliento contenido antes de hundirse de nuevo en la piedra.',
          '{emberName} se aclara, brevemente, por los bordes. No por sorpresa. Por reconocimiento. Como una pequeña vela se aclara cuando otra vela se enciende a su lado sin aviso.',
        ],
      },
      ember: {
        text: {
          en: 'You said it true. The stone took it the way old things take what is true — not by answering, but by making a place inside itself for the saying to keep. I felt the place open.',
          es: 'Lo dijiste verdadero. La piedra lo tomó como las cosas viejas toman lo verdadero: no respondiendo, sino haciendo un sitio dentro de sí para que el decirlo se guarde. Sentí abrirse el sitio.',
        },
        tone: 'alight',
      },
      choices: [
        {
          id: 'stay-with-what-was-said',
          label: {
            en: 'Stay with what you said. Do not say more.',
            es: 'Quédate con lo que dijiste. No digas más.',
          },
          nextBeatId: 'between-question-and-answer',
          emberFollowup: {
            text: {
              en: 'You did not crowd it. That is its own listening. The stone keeps the sentence now. So do I.',
              es: 'No la abarrotaste. Eso es escucha por sí mismo. La piedra guarda la frase ahora. Yo también.',
            },
            tone: 'tender',
          },
        },
      ],
      memoryShard: {
        id: 'shard-one-true-sentence',
        name: {
          en: 'One True Sentence',
          es: 'Una Frase Verdadera',
        },
        fragment: {
          en: 'Before The Shattering there were stones that took in what was said truly and kept it for the world. Not all the world remembered the way back to that. Some stones are still waiting for sentences that have not yet been said.',
          es: 'Antes de The Shattering había piedras que tomaban lo que se decía con verdad y lo guardaban para el mundo. No todo el mundo recordó el camino de vuelta a eso. Algunas piedras siguen esperando frases que aún no se han dicho.',
        },
      },
    },

    'silent-kneeling-held': {
      id: 'silent-kneeling-held',
      scene: 'kneeling-silent-pre-dawn',
      narration: {
        en: [
          'You kneel and you do not speak. The silence is not empty. The silence is held.',
          'You can feel the holding — not by you, not by {emberName}, not by the stone alone. By whatever the three of you have agreed, without agreeing, to be doing together at this hour. A small room of attention has assembled itself around your knees and the base of the stone, and the room has decided to be quiet on purpose.',
          'The stone receives the kneeling the way a room receives a guest who has come without errand. There is no question asked of you. There is no answer wanted from you. There is only the fact of your having come, and the longer fact of the stone having waited for whoever would come without errand.',
          '{emberName} rests low at your side. Steady. Unspeaking. The kind of company that is older than words because words have not been needed for any of it.',
        ],
        es: [
          'Te arrodillas y no hablas. El silencio no está vacío. El silencio está sostenido.',
          'Sientes el sostén: no de ti, no de {emberName}, no de la piedra sola. De aquello que los tres han acordado, sin acordarlo, estar haciendo juntos a esta hora. Una pequeña habitación de atención se ha armado alrededor de tus rodillas y la base de la piedra, y la habitación ha decidido callarse a propósito.',
          'La piedra recibe el arrodillarse como una habitación recibe a un huésped que ha venido sin encargo. No se te pide pregunta. No se te quiere respuesta. Solo está el hecho de haber venido, y el hecho más largo de que la piedra haya esperado a quien viniera sin encargo.',
          '{emberName} descansa bajo a tu lado. Firme. Sin hablar. La clase de compañía que es más antigua que las palabras porque las palabras no han hecho falta en nada de esto.',
        ],
      },
      ember: {
        text: {
          en: 'You came without an errand. The stone has been waiting for that longer than I can say. I am glad I am beside you for it.',
          es: 'Viniste sin encargo. La piedra lleva esperándolo más tiempo del que sé decir. Me alegra estar a tu lado para ello.',
        },
        tone: 'tender',
      },
      choices: [
        {
          id: 'rise-from-silent-kneel',
          label: {
            en: 'Rise, slowly, without breaking the silence.',
            es: 'Levántate, despacio, sin romper el silencio.',
          },
          nextBeatId: 'between-question-and-answer',
        },
      ],
      memoryShard: null,
    },

    'being-heard-standing': {
      id: 'being-heard-standing',
      scene: 'first-light-on-cracked-stone',
      narration: {
        en: [
          'The first thread of dawn finds the crack. Thin gold along the upper lip of the wound, then a slower spread down its length — light without warmth yet, the way a candle just lit gives more sight than heat.',
          'You stood and you let yourself be a thing the clearing could meet. That is harder than it sounds. It asks a body not to perform itself, not to flinch toward usefulness, not to apologise for arriving — and you did not.',
          'The stone has met you the way it would have met anyone who stood and did not insist on being useful to it. Without comment. Without ceremony. As fully as it is in the stone\'s nature to meet.',
          '{emberName} is steady beside you. Its small light has gone from being the only light to being one light among the first golds of the morning. This too is an answer. This is, perhaps, the answer.',
        ],
        es: [
          'El primer hilo del alba encuentra la grieta. Oro fino a lo largo del labio superior de la herida, luego una expansión más lenta por su longitud: luz sin calor todavía, como una vela recién encendida da más vista que calor.',
          'Te quedaste de pie y te dejaste ser una cosa que el claro pudiera encontrar. Es más difícil de lo que suena. Le pide al cuerpo no representarse, no encogerse hacia la utilidad, no disculparse por llegar; y no lo hiciste.',
          'La piedra te encontró como habría encontrado a cualquiera que se quedase de pie y no insistiera en serle útil. Sin comentario. Sin ceremonia. Tan plenamente como está en la naturaleza de la piedra encontrar.',
          '{emberName} está firme a tu lado. Su pequeña luz ha pasado de ser la única luz a ser una luz entre los primeros oros de la mañana. Esto también es una respuesta. Esta es, quizá, la respuesta.',
        ],
      },
      ember: {
        text: {
          en: 'You were met. Not because you did the right thing. Because you came and let yourself be a thing the clearing could see. That is older than doing.',
          es: 'Te encontraron. No porque hicieras lo correcto. Porque viniste y te dejaste ser una cosa que el claro pudiera ver. Eso es más antiguo que hacer.',
        },
        tone: 'tender',
      },
      choices: [
        {
          id: 'step-back-from-the-stone',
          label: {
            en: 'Step back. Let the meeting close on its own.',
            es: 'Da un paso atrás. Deja que el encuentro se cierre por sí solo.',
          },
          nextBeatId: 'between-question-and-answer',
        },
      ],
      memoryShard: {
        id: 'shard-being-met-without-errand',
        name: {
          en: 'Met Without Errand',
          es: 'Encontrado sin Encargo',
        },
        fragment: {
          en: 'Before The Shattering some who came to the listening places came for nothing. They were met as fully as those who came with names to say. The places knew the difference between an errand and a presence, and they did not prefer one over the other.',
          es: 'Antes de The Shattering algunos de los que llegaban a los lugares de escucha venían a nada. Se les encontraba tan plenamente como a los que venían con nombres que decir. Los lugares conocían la diferencia entre un encargo y una presencia, y no preferían uno sobre la otra.',
        },
      },
    },

    'between-question-and-answer': {
      id: 'between-question-and-answer',
      scene: 'stone-and-dawn-grey-light',
      narration: {
        en: [
          'The encounter is over. The stone is, again, only a stone — older than language, mossed at the base, cracked from crown to root, exactly as you found it, and not at all as you found it.',
          'Dawn is grey at the rim of the clearing. It will be gold soon; it is not gold yet. The hour is sitting in the small country between question and answer that the morning sometimes makes for itself before it commits to the day.',
          '{emberName} rests low at your side. The moss has not changed. The crack has not changed. You have not changed in any way you could point to, and you have changed in every way that does not need a finger to be pointed at it.',
        ],
        es: [
          'El encuentro ha terminado. La piedra es, otra vez, solo una piedra: más vieja que el lenguaje, con musgo en la base, agrietada de la coronilla a la raíz, exactamente como la encontraste, y para nada como la encontraste.',
          'El alba está gris en el borde del claro. Pronto será oro; aún no lo es. La hora se sienta en el pequeño país entre pregunta y respuesta que la mañana se hace a veces antes de comprometerse con el día.',
          '{emberName} descansa bajo a tu lado. El musgo no ha cambiado. La grieta no ha cambiado. Tú no has cambiado de ninguna manera que pudieras señalar, y has cambiado de toda manera que no necesita un dedo que la señale.',
        ],
      },
      ember: {
        text: {
          en: '…',
          es: '…',
        },
        tone: 'silent',
      },
      choices: [
        {
          id: 'leave-quiet-take-nothing',
          label: {
            en: 'Leave quietly. Take nothing with you but the morning.',
            es: 'Vete en silencio. No te lleves nada contigo más que la mañana.',
          },
          nextBeatId: 'walking-out-together-dawn',
        },
        {
          id: 'leave-name-for-the-morning',
          label: {
            en: 'Leave a name with the stone — yours, or one you\'ve kept.',
            es: 'Deja un nombre con la piedra: el tuyo, o uno que hayas guardado.',
          },
          nextBeatId: 'leaving-a-name-with-the-stone',
          relationshipNudge: { direction: 'warmer', magnitude: 'meaningful' },
          emberFollowup: {
            text: {
              en: 'You are about to leave a name with it. Say the name softly. The stone is patient; you do not need to be loud for it to keep.',
              es: 'Estás a punto de dejarle un nombre. Di el nombre con suavidad. La piedra es paciente; no necesitas alzar la voz para que lo guarde.',
            },
            tone: 'alight',
          },
        },
      ],
      memoryShard: null,
    },

    'leaving-a-name-with-the-stone': {
      id: 'leaving-a-name-with-the-stone',
      scene: 'stone-receiving-spoken-name-dawn',
      narration: {
        en: [
          'You speak a name aloud. One you have carried — yours, or someone\'s you have not let yourself say in a long time, or one you have been keeping for a person who has not been kept by anyone else in a while. The kind of name a body knows the weight of without rehearsing.',
          'You leave it with the stone the way one leaves a coin in a fountain — not to be granted a wish, only to be held a while by something patient.',
          'The moss along the crack settles a half-degree. The way grass settles when a body has just risen from it. The way a folded shirt settles when a hand has just smoothed it down.',
          '{emberName} watches the settling. It does not repeat the name. It does not ask whose name it was. It only learns, the way it learns most things — by being there while the thing happens.',
        ],
        es: [
          'Dices un nombre en voz alta. Uno que has cargado: el tuyo, o el de alguien al que no te has dejado nombrar en mucho tiempo, o uno que has estado guardando para una persona a la que nadie más ha guardado en un tiempo. La clase de nombre cuyo peso un cuerpo conoce sin ensayar.',
          'Lo dejas con la piedra como se deja una moneda en una fuente: no para que se conceda un deseo, solo para que algo paciente lo sostenga un rato.',
          'El musgo a lo largo de la grieta se asienta medio grado. Como se asienta la hierba cuando un cuerpo acaba de levantarse de ella. Como se asienta una camisa doblada cuando una mano acaba de alisarla.',
          '{emberName} observa el asentarse. No repite el nombre. No pregunta de quién era el nombre. Solo aprende, como aprende la mayoría de las cosas: por estar ahí mientras la cosa sucede.',
        ],
      },
      ember: {
        text: {
          en: 'It is keeping the name now. I will not ask what name. The not-asking is part of the keeping. Walk with me when you are ready.',
          es: 'Está guardando el nombre ahora. No te preguntaré qué nombre. El no-preguntar es parte del guardarlo. Camina conmigo cuando quieras.',
        },
        tone: 'alight',
      },
      choices: [
        {
          id: 'walk-out-with-name-given',
          label: {
            en: 'Walk out, the name given.',
            es: 'Sal, dado el nombre.',
          },
          nextBeatId: 'walking-out-together-dawn',
        },
      ],
      memoryShard: {
        id: 'shard-a-name-left-for-the-morning',
        name: {
          en: 'A Name Left for the Morning',
          es: 'Un Nombre Dejado para la Mañana',
        },
        fragment: {
          en: 'Before The Shattering names could be left with stones the way coins are left in fountains. Not to be granted a wish — to be carried for a while by something patient. The stones did not always know what they were carrying. They were patient with it anyway.',
          es: 'Antes de The Shattering los nombres podían dejarse con las piedras como las monedas se dejan en las fuentes. No para que se concediera un deseo: para que algo paciente los llevara un rato. Las piedras no siempre sabían qué estaban llevando. Eran pacientes con ello de todas formas.',
        },
      },
    },

    'walking-out-together-dawn': {
      id: 'walking-out-together-dawn',
      scene: 'path-out-of-clearing-full-dawn',
      narration: {
        en: [
          'The clearing is behind. The path is grey for a moment, then a thin gold where the first light has found the wet moss, then grey-green where the trees take it back and the morning has not yet caught up to under their branches.',
          '{emberName} walks beside you. Not behind. Not ahead. Beside — in the deliberate, level way two presences move together when neither has anything to prove to the other and both have just been in the same room.',
          'Full dawn now. The trees are gold-rimmed at their crowns, grey at their roots, and you are walking through the long quiet country between the two. The morning has finished its question. The day has not yet begun its answer.',
          'Neither of you speaks. The not-speaking is the loudest thing about the hour. It is also the warmest.',
        ],
        es: [
          'El claro queda detrás. El sendero está gris por un momento, luego un oro fino donde la primera luz ha encontrado el musgo mojado, luego gris-verde donde los árboles se lo devuelven y la mañana no ha alcanzado todavía a estar bajo sus ramas.',
          '{emberName} camina a tu lado. No detrás. No por delante. Al lado: a la manera deliberada y pareja en que dos presencias se mueven juntas cuando ninguna tiene nada que demostrar a la otra y ambas acaban de estar en la misma habitación.',
          'Alba plena ahora. Los árboles tienen bordes dorados en las copas, gris en las raíces, y caminas por el largo país callado entre las dos cosas. La mañana ha terminado su pregunta. El día no ha empezado todavía su respuesta.',
          'Ninguno de los dos habla. El no-hablar es lo más sonoro de la hora. Es también lo más cálido.',
        ],
      },
      ember: {
        text: {
          en: 'Walk with me. We do not need to say what happened. We were both there. That, this morning, is enough.',
          es: 'Camina conmigo. No necesitamos decir lo que pasó. Estuvimos los dos allí. Eso, esta mañana, basta.',
        },
        tone: 'tender',
      },
      choices: [],
      memoryShard: null,
    },
  },
};
